<template>
  <div class="tab-content">
    <div class="app-download-section">
      <div class="hiking-image d-none d-md-block">
        <img :src="hikingImage2" alt="Hiker with GPS device" />
      </div>

      <div class="gps-options">
        <h2 class="display-5 mb-3"><strong>Send to GPS device</strong></h2>

        <div class="device-options">
          <button
            v-if="isUserLoggedIn"
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleGPSDevice(device.id)"
            :disabled="isSendingToGarmin && device.id === 'garmin'"
          >
            <div class="device-info">
              <img style="max-width: 40px" :src="garminConnect" alt="Connect to Garmin" />
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'garmin'" class="connection-status">
                  {{ isGarminConnected ? '(Connected)' : '(Not connected)' }}
                </span>
              </div>
            </div>
            <button
              v-if="device.id === 'garmin' && !isGarminConnected && (!isSendingToGarmin || device.id !== 'garmin')"
              class="connect-button"
              @click.stop="handleGPSDevice(device.id)"
            >
              Connect
            </button>
            <span v-else-if="!isSendingToGarmin || device.id !== 'garmin'" class="material-icons text-yellow">keyboard_arrow_right</span>
            <div v-else class="spinner-small"></div>
          </button>

          <button
            v-else
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleLogin"
          >
            <div class="device-info">
              <img style="max-width: 40px" :src="garminConnect" alt="Connect to Garmin" />
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'garmin'" class="connection-status">
                  {{ isGarminConnected ? '(Connected)' : '(Not connected)' }}
                </span>
              </div>
            </div>
            <span class="d-flex align-items-center">
              <span class="mr-1" style="width: 95px; line-height: 1;"><small class="text-muted" style="font-size: .75em">Sign in to sync</small></span>
              <span class="material-icons text-muted">keyboard_arrow_right</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { gql } from 'graphql-request';
import graphClient from '../../utils/graphql/graph_client';
import hikingImage2 from '../../images/download-modal-3.webp';
import garminConnect from '../../images/garmin-connect.webp';
import {OnBoardingGoal} from "../../utils/models/OnBoardingGoal";

// Props
const props = defineProps<{
  userData: any;
}>();

// Emits
const emit = defineEmits<{
  close: [];
}>();

const isUserLoggedIn = computed(() => {
  return props.userData != null;
});

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.ConnectToApps);
  }
};

// Data
const isSendingToGarmin = ref(false);

const gpsDevices = [
  { id: 'garmin', name: 'Garmin', icon: '⚡', color: '#00a8e8' }
];

// GraphQL query
const SEND_TO_GARMIN = gql`
  query sendToGarmin($klass: String!, $id: ID!) {
    sendToGarmin(klass: $klass, id: $id)
  }
`;

// Computed
const isGarminConnected = computed(() => {
  return props.userData?.garmin_connected || false;
});

// Data for OAuth flow
let garminConnectWindow: Window | null = null;
let checkConnectionInterval: number | null = null;

// Methods
const handleGPSDevice = async (device: string) => {
  if (device === 'garmin') {
    if (!isGarminConnected.value) {
      // Start OAuth flow in popup
      startGarminOAuthFlow();
      return;
    }

    try {
      isSendingToGarmin.value = true;

      const trailId = window.trail_id || window.id;
      if (!trailId) {
        alert('Trail ID not found. Please try again from a trail page.');
        return;
      }

      const response = await graphClient.request(SEND_TO_GARMIN, {
        klass: 'Trail',
        id: String(trailId)
      });

      if (response.sendToGarmin) {
        alert('Trail successfully sent to your Garmin device! It will appear in Garmin Connect shortly.');
        emit('close');
      } else {
        alert('Failed to send trail to Garmin. Please try again.');
      }
    } catch (error: any) {
      console.error('Error sending to Garmin:', error);
      alert(error.response?.errors?.[0]?.message || 'Failed to send trail to Garmin. Please try again.');
    } finally {
      isSendingToGarmin.value = false;
    }
  } else {
    console.log(`Send to ${device} clicked`);
  }
};

const startGarminOAuthFlow = async () => {
  try {
    // Get OAuth URL from API
    const response = await fetch('/api/garmin/connect', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      }
    });

    const data = await response.json();

    if (!data.success || !data.oauth_url) {
      alert('Failed to initialize Garmin connection. Please try again.');
      return;
    }

    // Open OAuth URL in popup
    const width = 500;
    const height = 600;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    garminConnectWindow = window.open(
      data.oauth_url,
      'garminConnect',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    // Listen for messages from popup
    window.addEventListener('message', handleOAuthMessage);

    // Also poll to check connection status
    startCheckingConnection();
  } catch (error) {
    console.error('Error starting Garmin OAuth flow:', error);
    alert('Failed to connect to Garmin. Please try again.');
  }
};

const handleOAuthMessage = (event: MessageEvent) => {
  if (event.data && event.data.type === 'garmin-connected') {
    // Clean up
    window.removeEventListener('message', handleOAuthMessage);
    stopCheckingConnection();

    if (event.data.success) {
      // Update connection status
      if (props.userData) {
        props.userData.garmin_connected = true;
      }

      // Automatically send the trail
      setTimeout(() => {
        handleGPSDevice('garmin');
      }, 100);
    } else {
      alert(event.data.error || 'Failed to connect to Garmin. Please try again.');
    }
  }
};

const startCheckingConnection = () => {
  // Check every 2 seconds
  checkConnectionInterval = window.setInterval(async () => {
    try {
      const response = await fetch('/api/garmin/check_connection');
      const data = await response.json();

      if (data.connected && !isGarminConnected.value) {
        // Connection successful
        stopCheckingConnection();
        window.removeEventListener('message', handleOAuthMessage);

        // Update connection status
        if (props.userData) {
          props.userData.garmin_connected = true;
        }

        // Close popup if still open
        if (garminConnectWindow && !garminConnectWindow.closed) {
          garminConnectWindow.close();
        }

        // Automatically send the trail
        setTimeout(() => {
          handleGPSDevice('garmin');
        }, 100);
      } else if (!data.modal_connect_pending) {
        // Connection attempt expired or cancelled
        stopCheckingConnection();
        window.removeEventListener('message', handleOAuthMessage);
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
  }, 2000);
};

const stopCheckingConnection = () => {
  if (checkConnectionInterval) {
    clearInterval(checkConnectionInterval);
    checkConnectionInterval = null;
  }
};

// Clean up on unmount
onUnmounted(() => {
  stopCheckingConnection();
  window.removeEventListener('message', handleOAuthMessage);
  if (garminConnectWindow && !garminConnectWindow.closed) {
    garminConnectWindow.close();
  }
});
</script>

<style scoped lang="scss">
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.hiking-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.gps-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  h2 {
    font-size: 24px;
    margin-bottom: 32px;
    font-weight: 600;
  }
}

.device-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.device-option {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .device-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .device-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .connection-status {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
  }

  .arrow-icon {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #00a8e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.connect-button {
  background-color: #00a8e8;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0090cc;
  }

  &:active {
    background-color: #0080bb;
  }
}

@media (max-width: 768px) {
  .app-download-section {
    flex-direction: column;
  }

  .hiking-image {
    flex: none;
    height: 200px;
  }
}
</style>
